import { Component, ErrorInfo, ReactNode } from 'react';

import { Button } from '@/components/ui/Button';
import { captureException, addBreadcrumb } from '@/lib/sentry';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Store error info in state
    this.setState({ errorInfo });

    // Add breadcrumb for debugging context
    addBreadcrumb({
      category: 'error-boundary',
      message: 'React error boundary triggered',
      level: 'error',
      data: {
        componentStack: errorInfo.componentStack,
      },
    });

    // Send to Sentry (only in production)
    captureException(error, {
      componentStack: errorInfo.componentStack,
    });
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default fallback UI with industrial design
      return (
        <div className="min-h-screen bg-black flex items-center justify-center px-4 py-8">
          <div className="max-w-2xl w-full">
            {/* Error Container */}
            <div className="relative border-2 border-neon-red bg-industrial-gray p-8 md:p-12">
              {/* Corner Accents */}
              <div className="absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 border-neon-red"></div>
              <div className="absolute top-0 right-0 w-4 h-4 border-t-2 border-r-2 border-neon-red"></div>
              <div className="absolute bottom-0 left-0 w-4 h-4 border-b-2 border-l-2 border-neon-red"></div>
              <div className="absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2 border-neon-red"></div>

              {/* Error Icon */}
              <div className="text-center mb-8">
                <div className="inline-block relative">
                  <div className="text-8xl animate-pulse-fast">⚠️</div>
                  <div className="absolute inset-0 bg-neon-red blur-3xl opacity-20 animate-pulse"></div>
                </div>
              </div>

              {/* Error Title */}
              <h1 className="text-4xl md:text-5xl font-bold text-white font-sans uppercase leading-tight md:leading-tight tracking-tight text-center mb-4">
                Valami <span className="text-neon-red">elromlott</span>
              </h1>

              {/* Error Subtitle */}
              <p className="text-neutral-400 text-lg text-center mb-8 font-body">
                Sajnáljuk, de váratlan hiba történt a betöltés során.
                <br />A fejlesztőket értesítettük a problémáról.
              </p>

              {/* Development Mode: Show Error Details */}
              {import.meta.env.DEV && this.state.error && (
                <div className="mt-6 mb-8 p-4 bg-black border border-neutral-800">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-2 h-2 bg-neon-red animate-pulse-fast"></div>
                    <p className="text-xs text-neon-red font-mono uppercase tracking-wider">
                      Development Mode - Error Details
                    </p>
                  </div>
                  <div className="space-y-2">
                    <p className="text-sm text-white font-mono break-words">
                      <span className="text-neutral-400">Error:</span> {this.state.error.toString()}
                    </p>
                    {this.state.error.stack && (
                      <details className="mt-2">
                        <summary className="text-xs text-neutral-400 font-mono cursor-pointer hover:text-neon-red">
                          Stack trace
                        </summary>
                        <pre className="text-xs text-neutral-400 font-mono mt-2 overflow-x-auto whitespace-pre-wrap">
                          {this.state.error.stack}
                        </pre>
                      </details>
                    )}
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
                <Button size="lg" onClick={this.handleGoHome}>
                  ← Vissza a főoldalra
                </Button>

                <Button variant="secondary" size="lg" onClick={this.handleReset}>
                  Újratöltés
                </Button>
              </div>

              {/* Additional Help Text */}
              <p className="text-xs text-neutral-400 text-center mt-8 font-mono">
                Ha a probléma továbbra is fennáll, kérjük frissítsd az oldalt vagy próbáld újra
                később.
              </p>
            </div>

            {/* Glow Effect */}
            <div className="absolute inset-0 bg-neon-red opacity-5 blur-3xl -z-10"></div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
